import React from 'react';
import { GalleryImage } from '../types/contractor';
import { API_BASE } from '../api/config';

const API_BASE_URL = `${API_BASE}/api`;

interface GalleryOfWorksEditableProps {
  images: GalleryImage[];
  onChange: (images: GalleryImage[]) => void;
}

const GalleryOfWorksEditable: React.FC<GalleryOfWorksEditableProps> = ({ images, onChange }) => {
  const [uploading, setUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState('');

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_BASE_URL}/Upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      onChange([...images, { url: data.url, caption: '' }]);
    } catch {
      setUploadError('Upload failed. Try again.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleCaptionChange = (index: number, caption: string) => {
    const updated = images.map((img, i) => i === index ? { ...img, caption } : img);
    onChange(updated);
  };

  const handleDelete = (index: number) => {
    onChange(images.filter((_, i) => i !== index));
  };

  return (
    <section className="mb-8 border-b pb-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">Gallery of Works</h2>
      {images.length === 0 && (
        <p className="text-sm text-gray-400 mb-4">No photos yet. Upload images of your past work to showcase your quality.</p>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {images.map((img, i) => (
          <div key={i} className="flex flex-col gap-1">
            <div className="relative">
              <img className="w-full h-40 object-cover rounded-lg shadow-sm" src={img.url} alt={img.caption || 'Gallery image'} />
              <button
                type="button"
                className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700"
                onClick={() => handleDelete(i)}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <input
              type="text"
              className="w-full p-1 text-xs border border-gray-300 rounded"
              placeholder="Caption (optional)"
              value={img.caption ?? ''}
              onChange={(e) => handleCaptionChange(i, e.target.value)}
            />
          </div>
        ))}
        <div className="flex items-center justify-center w-full h-40 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-500 transition-colors cursor-pointer">
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            id="gallery-upload"
            onChange={handleFileChange}
            disabled={uploading}
          />
          <label htmlFor="gallery-upload" className="flex flex-col items-center cursor-pointer p-4">
            {uploading ? (
              <span className="material-symbols-outlined text-[32px] animate-spin">progress_activity</span>
            ) : (
              <>
                <svg className="w-8 h-8 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                <span className="text-sm">Upload image</span>
              </>
            )}
          </label>
        </div>
      </div>
      {uploadError && <p className="text-red-500 text-sm mt-2">{uploadError}</p>}
    </section>
  );
};

export default GalleryOfWorksEditable;
